import { cva } from "class-variance-authority";
import type { CSSProperties } from "react";
import { colors } from "../../theme/colors";
import type { ButtonSize, ButtonVariant } from "./types";

const baseButtonClasses =
  "relative inline-flex items-center justify-center cursor-pointer font-medium transition-all duration-150 will-change-[padding] ring-primary-100 disabled:cursor-not-allowed text-center truncate *:transition-opacity outline-hidden focus-visible:ring-3";
const baseIconButtonClasses =
  "relative inline-flex justify-center items-center cursor-pointer transition-all duration-150 disabled:cursor-default ring-primary-100 outline-hidden focus-visible:ring-3";

const secondaryGrayClasses =
  "ring-grey-200/80 border-grey-300 border bg-white disabled:bg-white text-grey-700 hover:text-grey-800 hover:bg-grey-50 shadow-xs disabled:border-grey-200 disabled:text-grey-300 focus-visible:ring-3 data-[state='open']:bg-grey-50 data-[state='open']:text-grey-800 data-[state='open']:border-grey-300";
const primaryClasses =
  "shadow-primary-md bg-primary-500 text-white hover:bg-primary-600 disabled:bg-primary-200";
const tertiaryGrayClasses =
  "text-grey-500 border border-grey-100/0 hover:bg-grey-50 hover:text-grey-600 disabled:text-grey-300 disabled:hover:bg-white/0 focus-visible:ring-grey-200/75 data-[state='open']:bg-grey-50 data-[state='open']:text-grey-600";
const tertiaryColorClasses =
  "text-primary-700 border border-primary-100/0 hover:bg-primary-50 hover:text-primary-800 disabled:text-grey-300";
const secondaryColorClasses =
  "bg-primary-50 text-primary-700 hover:text-primary-800 hover:bg-primary-100 disabled:bg-primary-25 disabled:text-primary-300";
const destructiveClasses =
  "bg-error-600 text-white hover:bg-error-700 disabled:bg-error-200 focus-visible:ring-error-100";
const linkColorClasses =
  "p-0! text-primary-700 hover:text-primary-800 disabled:text-grey-300 rounded-none!";
const linkGrayClasses =
  "p-0! text-grey-500 hover:text-grey-600 disabled:text-grey-300 rounded-none!";

const warningClasses =
  "shadow-xs bg-warning-600 text-white hover:bg-warning-700 disabled:bg-warning-200 outline-hidden focus-visible:ring-3 ring-warning-300";

export const buttonVariants = cva(baseButtonClasses, {
  variants: {
    variant: {
      primary: primaryClasses,
      secondaryGray: secondaryGrayClasses,
      secondaryColor: secondaryColorClasses,
      tertiaryGray: tertiaryGrayClasses,
      tertiaryColor: tertiaryColorClasses,
      destructive: destructiveClasses,
      linkcolor: linkColorClasses,
      linkgray: linkGrayClasses,
      warning: warningClasses,
    },
    size: {
      xs: "text-xs rounded-md px-3 py-2 gap-1.5",
      sm: "text-sm rounded-lg gap-2 px-3.5 py-2",
      md: "text-sm rounded-lg gap-2 px-4 py-2.5",
      lg: "text-base rounded-[10px] gap-2 px-[18px] py-2.5",
      xl: "text-base rounded-xl gap-2 py-3 px-5",
      "2xl": "text-lg rounded-xl gap-3 py-4 px-7",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "secondaryGray",
  },
});

export const iconButtonVariants = cva(baseIconButtonClasses, {
  variants: {
    variant: {
      primary: primaryClasses,
      secondaryGray: secondaryGrayClasses,
      secondaryColor: secondaryColorClasses,
      tertiaryGray: tertiaryGrayClasses,
      tertiaryColor: tertiaryColorClasses,
      destructive: destructiveClasses,
      linkcolor: linkColorClasses,
      linkgray: linkGrayClasses,
      warning: warningClasses,
    },
    size: {
      xs: "p-2 rounded-md",
      sm: "p-2 rounded-lg",
      md: "p-2.5 rounded-lg",
      lg: "p-3 rounded-lg",
      xl: "p-3.5 rounded-lg",
      "2xl": "p-5 rounded-lg",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "secondaryGray",
  },
});

export const buttonIconVariants = cva("stroke-current shrink-0", {
  variants: {
    size: {
      xs: "h-4 w-4",
      sm: "h-5 w-5",
      md: "h-5 w-5",
      lg: "h-5 w-5",
      xl: "h-5 w-5",
      "2xl": "h-6 w-6",
    },
  },
});

export const loadingSpinnerColor: Record<ButtonVariant, string> = {
  primary: colors.white,
  secondaryGray: colors.grey[500],
  secondaryColor: colors.primary[700],
  tertiaryGray: colors.primary[600],
  tertiaryColor: colors.primary[700],
  destructive: colors.white,
  linkcolor: colors.primary[600],
  linkgray: colors.grey[500],
  warning: colors.white,
};

export const loadingSpinnerSize: Record<ButtonSize, CSSProperties> = {
  xs: { width: 13, height: 13 },
  sm: { width: 14, height: 14 },
  md: { width: 16, height: 16 },
  lg: { width: 18, height: 18 },
  xl: { width: 20, height: 20 },
  "2xl": { width: 22, height: 22 },
};
